/**
 * EscolaFichaPage — rota /escolas/:inep (RF-M7-003, US-08 AC2).
 * Cabeçalho com dados oficiais INEP + coordenada, linha do tempo de visitas e
 * lista de outros documentos. Consome GET /escolas/{inep} real.
 * ADR-038: cada visita da linha do tempo com a etiqueta da evidência; a linha da planilha cuja escola tem termo
 * aparece aqui marcada "Referência — há termo com imagem" (Req 4.3).
 * ADR-046: a evidência diz a fonte (planilha de controle, Relatório Fotográfico); a seção "Nas planilhas de controle"
 * mostra as linhas da base de controle da escola (os campos mapeados; a visita que a linha criou).
 */

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEscola } from '../hooks/useConsulta';
import { MarcadorPill, nomesDiferem } from '../components/ConsultaShared';
import { EtiquetaEvidencia } from '../components/Evidencia';
import { LinhaControle, VisitaTimeline } from '../services/consulta-types';
import { formatarDataVisita } from '../components/format';

function Timeline({ visitas }: { visitas: readonly VisitaTimeline[] }): JSX.Element {
  if (visitas.length === 0) {
    return <p className="muted">Nenhuma visita registrada.</p>;
  }
  return (
    <ul className="timeline">
      {visitas.map((v) => (
        <li key={v.id}>
          <div>
            <Link to={`/visitas/${v.id}`}>{v.dataVisita ? formatarDataVisita(v.dataVisita) : 'data desconhecida'}</Link>{' '}
            <MarcadorPill marcador={v.marcador} />{' '}
            <EtiquetaEvidencia evidencia={v.evidencia} referencia={v.referencia === true} fonte={v.fonte} />
          </div>
          <div className="muted">{v.resultado ?? 'resultado não informado'}</div>
        </li>
      ))}
    </ul>
  );
}

/** ADR-046: a situação normalizada da linha da base de controle, em palavras. */
export const ROTULO_SITUACAO_CONTROLE: Readonly<Record<LinhaControle['situacao'], string>> = {
  produtiva: 'Produtiva',
  improdutiva: 'Improdutiva',
  desativada: 'Escola desativada',
  nao_realizada: 'Não realizada',
  desconhecida: 'Sem situação de visita',
};

const numeroBr = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 });

function NasPlanilhasDeControle({ linhas }: { readonly linhas: readonly LinhaControle[] }): JSX.Element {
  return (
    <section className="card" aria-label="Nas planilhas de controle">
      <h2>Nas planilhas de controle</h2>
      <table className="table">
        <thead>
          <tr>
            <th>Planilha</th>
            <th>Situação</th>
            <th>Data</th>
            <th>Coordenada</th>
            <th>Velocidade (Down/Up)</th>
            <th>Outros</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={`${l.arquivo}|${l.aba}|${l.linha}`}>
              <td>
                <span className="mono">{l.arquivo}</span>
                <div className="muted">{`${l.tipo === 'designacao' ? 'Lista de designação' : 'Planilha de controle'} · aba ${l.aba}, linha ${l.linha}`}</div>
              </td>
              <td>
                {ROTULO_SITUACAO_CONTROLE[l.situacao] ?? l.situacao}
                {l.statusOriginal && <div className="muted">«{l.statusOriginal}»</div>}
                {l.motivo && <div className="muted">{l.motivo}</div>}
              </td>
              <td>{l.data ? formatarDataVisita(l.data) : '—'}</td>
              <td>{l.latitude !== null && l.longitude !== null ? `${numeroBr.format(l.latitude)}, ${numeroBr.format(l.longitude)}` : '—'}</td>
              <td>{l.downMbps !== null || l.upMbps !== null ? `${l.downMbps ?? '—'} / ${l.upMbps ?? '—'} Mbps` : '—'}</td>
              <td>
                {[l.fornecedor, l.fase && `fase ${l.fase}`, l.lote && `lote ${l.lote}`, l.designacao && `designação ${l.designacao}`]
                  .filter(Boolean)
                  .join(' · ') || '—'}
                {l.visitaId && (
                  <div>
                    <Link to={`/visitas/${l.visitaId}`}>Visita criada por esta linha</Link>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function EscolaFichaPage(): JSX.Element {
  const { inep } = useParams<{ inep: string }>();
  const { ficha, carregando, erro } = useEscola(inep ?? null);
  const navigate = useNavigate();

  return (
    <div>
      <p className="breadcrumb">
        <Link to="/escolas">Escolas</Link>
        <span className="sep">/</span>
        <span>{inep}</span>
      </p>

      {carregando && <p className="muted">Carregando…</p>}
      {erro && <p className="notice">Não foi possível carregar a escola (ou INEP inexistente).</p>}

      {ficha && (
        <>
          <div className="card">
            <h1 className="page-title">{ficha.nome}</h1>
            {/* ADR-035: o oficial é o título; o nome como lido no termo logo abaixo, quando difere. */}
            {nomesDiferem(ficha.nomeTermo, ficha.nome) && (
              <p className="nome-secundario">No termo: «{ficha.nomeTermo}»</p>
            )}
            <p className="page-subtitle">
              INEP <span className="mono">{ficha.inep}</span> · {ficha.municipio ?? '—'}
              {ficha.uf ? ` / ${ficha.uf}` : ''}
              {ficha.coordenada.lat !== null && ficha.coordenada.lng !== null
                ? ` · ${ficha.coordenada.lat.toFixed(4)}, ${ficha.coordenada.lng.toFixed(4)}`
                : ''}
            </p>
            <div className="row-actions">
              <button
                className="btn btn-ghost"
                onClick={() =>
                  navigate('/exportacoes', { state: { escopo: 'escola', filtro: { escolaInep: ficha.inep } } })
                }
              >
                Exportar visitas desta escola
              </button>
            </div>
          </div>

          <section className="card">
            <h2>Linha do tempo de visitas</h2>
            <Timeline visitas={ficha.visitas} />
          </section>

          {ficha.outrosDocumentos.length > 0 && (
            <section className="card">
              <h2>Outros documentos</h2>
              <Timeline visitas={ficha.outrosDocumentos} />
            </section>
          )}

          {(ficha.nasPlanilhasDeControle?.length ?? 0) > 0 && <NasPlanilhasDeControle linhas={ficha.nasPlanilhasDeControle!} />}
        </>
      )}
    </div>
  );
}
